import styled from "styled-components";

/**
 * v3 hero: a two-column bone panel for the direct-mail preapproval flow.
 * Ink, not red or blue, is the interactive accent here; red is reserved for the
 * eyebrow, the headline rule and the section kickers.
 *
 * Sizing is px, not rem — styles/03-elements/base-font-sizing.scss drops the
 * root from 62.5% to 50% below 992px, so rem would silently shrink 20% on
 * mobile and push the inputs under the 16px that stops iOS zooming on focus.
 * Breakpoints follow Tailwind's sm = 640px / lg = 1024px, min-width first.
 */
export const Wrapper = styled.section`
  position: relative;
  overflow: hidden;
  background: var(--brand-bone);
`;

/** oversized red feather bleeding off the right edge */
export const Feather = styled.div`
  pointer-events: none;
  position: absolute;
  right: -64px;
  top: 64px;
  width: 520px;
  height: 520px;
  transform: rotate(12deg);
  opacity: 0.06;
  color: var(--brand-red);

  svg {
    width: 100%;
    height: 100%;
    display: block;
  }

  @media screen and (min-width: 1024px) {
    width: 720px;
    height: 720px;
  }
`;

export const Inner = styled.div`
  position: relative;
  margin: 0 auto;
  max-width: 1152px;
  display: grid;
  gap: 40px;
  padding: 48px 20px 96px;

  /* grid children default to min-width:auto and refuse to shrink below their
   * min-content, which overflows the panel on narrow screens */
  > * {
    min-width: 0;
  }

  @media screen and (min-width: 640px) {
    padding: 64px 32px 96px;
  }

  @media screen and (min-width: 1024px) {
    grid-template-columns: 1.05fr 1fr;
    align-items: center;
    gap: 56px;
    padding-bottom: 112px;
  }
`;

export const Copy = styled.div`
  .eyebrow {
    margin: 0 0 24px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border-radius: 999px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    background: #fff;
    padding: 6px 16px;
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.16em;
    color: rgba(14, 17, 22, 0.7);

    svg {
      width: 16px;
      height: 14px;
      flex-shrink: 0;
      color: var(--brand-red);
    }
  }

  .headline {
    margin: 0;
    font-family: var(--brand-display);
    font-size: 48px;
    font-weight: 800;
    line-height: 1.02;
    letter-spacing: -0.025em;
    color: var(--brand-ink);

    .line {
      display: block;
    }

    .marked {
      position: relative;
      display: inline-block;

      > span {
        position: relative;
        z-index: 1;
      }

      svg {
        position: absolute;
        left: 0;
        bottom: -8px;
        width: 100%;
        height: 12px;
        color: var(--brand-red);
      }
    }
  }

  .subhead {
    margin: 24px 0 0;
    max-width: 512px;
    font-size: 18px;
    line-height: 1.625;
    color: rgba(14, 17, 22, 0.7);

    strong {
      font-weight: 600;
      color: var(--brand-ink);
    }
  }

  .checks {
    margin: 32px 0 0;
    padding: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 10px;
    font-size: 14px;
    color: rgba(14, 17, 22, 0.8);
  }

  .check {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .check-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    border-radius: 999px;
    background: rgba(34, 197, 143, 0.2);
    box-shadow: 0 0 0 1px rgba(34, 197, 143, 0.4);

    svg {
      width: 12px;
      height: 12px;
      color: var(--brand-green-deep);
    }
  }

  .pills {
    margin: 32px 0 0;
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: rgba(14, 17, 22, 0.5);
  }

  .pill {
    border-radius: 999px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    background: #fff;
    padding: 6px 12px;
  }

  @media screen and (min-width: 640px) {
    .headline {
      font-size: 60px;
    }
  }

  @media screen and (min-width: 1024px) {
    .headline {
      font-size: 76px;
    }
  }

  @media screen and (max-width: 400px) {
    .headline {
      font-size: 40px;
    }
  }
`;

export const CardWrap = styled.div`
  position: relative;
  scroll-margin-top: 96px;
`;

/** two stickers straddling the card's top corners */
export const Sticker = styled.span`
  pointer-events: none;
  position: absolute;
  top: -12px;
  z-index: 10;
  padding: 8px 14px;
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.25);

  &.sticker-left {
    left: -12px;
    transform: rotate(-6deg);
    border-radius: 16px;
    background: var(--brand-ink);
    color: #fff;
  }

  &.sticker-right {
    right: -12px;
    transform: rotate(6deg);
    border-radius: 999px;
    background: var(--brand-green);
    color: var(--brand-ink);
  }
`;

export const Card = styled.div`
  position: relative;
  border-radius: 28px;
  border: 1px solid rgba(0, 0, 0, 0.05);
  background: #fff;
  padding: 24px;
  color: var(--brand-ink);
  text-align: left;
  box-shadow: 0 30px 60px -15px rgba(14, 17, 22, 0.25);

  .card-eyebrow {
    margin: 0 0 4px;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--brand-red);
  }

  .card-heading {
    margin: 0;
    font-family: var(--brand-display);
    font-size: 24px;
    font-weight: 800;
    line-height: 1.25;
    color: var(--brand-ink);

    .accent {
      color: var(--brand-red);
    }
  }

  .card-note {
    margin: 8px 0 0;
    font-size: 14px;
    color: rgba(14, 17, 22, 0.6);
  }

  .card-form {
    margin-top: 24px;
  }

  .mail-note {
    margin: 4px 0 0;
    text-align: center;
    font-size: 12px;
    color: rgba(14, 17, 22, 0.5);

    a {
      display: inline-block;
      font-weight: 600;
      color: var(--brand-ink);
      text-decoration: none;
      text-underline-offset: 4px;
      /* widen the tap target without moving the caption */
      padding: 6px 4px;
      margin: -6px -4px;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  @media screen and (min-width: 640px) {
    padding: 32px;

    .card-heading {
      font-size: 30px;
    }
  }
`;
